import { css } from '@/styled-system/css';
import { cva } from '@/styled-system/css/cva.mjs';

// We need to use string literals directly for Panda CSS to analyze the values at build time
// These values must match all possible options in the ListingQualityRating enum
const LISTING_QUALITY_RATINGS = {
  OUTSTANDING: 'OUTSTANDING',
  GOOD: 'GOOD',
  NEEDS_IMPROVEMENT: 'NEEDS_IMPROVEMENT',
  MISSING_CRITICAL_DETAILS: 'MISSING_CRITICAL_DETAILS',
} as const;

const TABLE_CELL_BORDER = '1px solid {colors.Table.Cell.border}';

export const pageHeadingStyles = css.raw({
  width: '100%',
  backgroundColor: 'bg.screen.neutral',
  display: 'flex',
  flexDirection: { base: 'column', lg: 'row' },
  justifyContent: 'space-between',
  alignItems: { base: 'start', lg: 'end' },
  gap: '400',
  padding: {
    base: '600',
    xl: '{spacing.600} {spacing.1200}',
  },
});

export const headingButtonStyles = css.raw({
  display: 'flex',
  gap: '800',
  height: { base: '{lineHeights.responsive.heading-lg.mobile}', lg: '{lineHeights.responsive.heading-lg.tablet}' },
});

export const filterStyles = css.raw({
  display: 'flex',
  flexDirection: 'column',
  marginTop: { base: '600', lg: '900' },
  gap: { base: '200', lg: '600' },
});

export const photoStyles = css.raw({
  borderRadius: '200',
  marginRight: {
    base: '200',
    lg: '400',
  },
  height: '100%',
  minHeight: '1800',
  width: '2600',
  display: { base: 'none', sm: 'block' },
  alignSelf: 'center',
  flexShrink: 0,
  transition: 'all 0.3s ease-out',
  _loading: {
    color: 'transparent',
    background: '#f1f1f4 linear-gradient(to right, #f1f1f4, #f9f9fb, #f1f1f4) no-repeat 100%',
    opacity: 'inherit',
    animation: 'shimmer 1.5s linear infinite',
  },
  _motionReduce: {
    animation: 'none',
  },
});

export const addressWrapperStyles = css.raw({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

export const addressTextStyles = css.raw({
  maxWidth: {
    base: 'calc(100vw - 152px)', // Responsive on small screens
    lg: '300px',
  },
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const statusIconStyles = css.raw({
  marginRight: '200',
  // oxlint-disable-next-line constellation/no-raw-spacing-values
  marginTop: '5px',
});

export const listingQualityContainerStyles = css.raw({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
});

export const listingQualityContentRecipe = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'ellipse',
    fontWeight: 'bold',
    fontSize: 'body',
    height: '1200',
    width: '1200',
  },
  variants: {
    rating: {
      [LISTING_QUALITY_RATINGS.OUTSTANDING]: {
        color: 'text.express.insight.strong',
        backgroundColor: 'bg.feedback.success.soft',
      },
      [LISTING_QUALITY_RATINGS.GOOD]: {
        color: 'text.express.insight.strong',
        backgroundColor: 'bg.feedback.success.soft',
      },
      [LISTING_QUALITY_RATINGS.NEEDS_IMPROVEMENT]: {
        color: 'text.accent.yellow.hero',
        backgroundColor: 'bg.feedback.warning.soft',
      },
      [LISTING_QUALITY_RATINGS.MISSING_CRITICAL_DETAILS]: {
        color: 'text.express.empower.strong',
        backgroundColor: 'bg.feedback.notify.soft',
      },
    },
  },
});

export const errorContainerStyles = css.raw({
  borderRadius: '200',
  minHeight: '5000',
  // oxlint-disable-next-line constellation/no-raw-color-values
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'space-around',
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '600',
  border: '{borderWidths.25} solid {colors.border.muted-alt}',
  marginTop: '600',
});

export const tableWrapperStyles = cva({
  base: {
    overflow: { base: 'auto', lg: 'hidden' },
    borderRadius: '200',
    borderBottomRadius: '0',
    borderLeft: TABLE_CELL_BORDER,
    borderRight: TABLE_CELL_BORDER,
    marginTop: { base: '600', lg: '900' },
  },
});

export const styledHeaderCellStyles = css.raw({
  borderTop: TABLE_CELL_BORDER,
  display: { base: 'none', lg: 'table-cell' },
  '&:first-child': {
    borderTopLeftRadius: '200',
    display: 'table-cell',
  },
  '&:last-child': {
    borderTopRightRadius: '200',
    display: 'table-cell',
  },
});

export const hiddenMobileTableCellStyles = css.raw({
  display: { base: 'none', lg: 'table-cell' },
  transition: 'background-color 0.1s ease-out',

  // When the parent tr is being hovered, the cell should be highlighted
  'tbody > *:hover tr &': {
    backgroundColor: 'bg.action.neutral.alt.hover',
  },
});

const leftDataCellRecipe = cva({
  base: {
    position: { base: 'relative', lg: 'sticky' },
    left: { base: 'auto', lg: 0 },
    zIndex: 2,
    boxSizing: 'border-box',
    transition: 'background-color 0.1s ease-out',
    verticalAlign: 'middle',
    maxWidth: { base: 'calc(100vw - 152px)', lg: 'none' }, // Constrain cell width on small screens
    overflow: 'hidden', // Prevent cell content from overflowing

    // When the parent tr is being hovered, the cell should be highlighted
    'tbody > *:hover tr &': {
      backgroundColor: 'bg.action.neutral.alt.hover',
    },

    // Bottom row is always rounded
    'tbody > *:last-child tr:last-of-type &': {
      borderBottom: TABLE_CELL_BORDER,
    },
    // Top row is rounded when header is hidden (mobile only)
    'tbody > *:first-child tr:first-of-type &': {
      lgDown: { borderTopLeftRadius: '200', borderTop: '2px solid {colors.border.muted-alt}' },
    },
  },
  variants: {
    scrolled: {
      true: {
        boxShadow: '2px 4px 8px 0px rgba(0, 0, 0, {opacity.15})',
        borderRight: '2px solid {colors.border.muted-alt}',
      },
    },
  },
});

export const leftDataCellStyles = (isScrolledToLeft: boolean) => leftDataCellRecipe.raw({ scrolled: isScrolledToLeft });

export const rightDataCellStyles = css.raw({
  transition: 'background-color 0.1s ease-out',

  // When the parent tr is being hovered, the cell should be highlighted
  'tbody > *:hover tr &': {
    backgroundColor: 'bg.action.neutral.alt.hover',
  },

  // Bottom row is always rounded
  'tbody > *:last-child tr:last-of-type &': {
    borderBottom: TABLE_CELL_BORDER,
  },
  // Top row is rounded when header is hidden (mobile only)
  'tbody > *:first-child tr:first-of-type &': {
    lgDown: { borderTopRightRadius: '200', borderTop: '2px solid {colors.border.muted-alt}' },
  },
});
